"use client";

import { useEffect, useState } from "react";
import { publicApiUrl } from "@frontend/lib/api";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import { PAYMENT_METHOD_LABELS } from "@creators/backend/lib/collabs/payment-methods";
import { formatDate } from "@creators/backend/lib/time";

type Proof = CollabRoom["proofs"][number];

/** What the payer submitted, in one line: thumbnail, method, reference, note. */
export function ProofRow({ collabId, proof }: { collabId: string; proof: Proof }) {
  const imageUrl = `${publicApiUrl()}/api/collabs/${collabId}/proofs/${proof.id}/image`;
  const meta = [
    PAYMENT_METHOD_LABELS[proof.method as keyof typeof PAYMENT_METHOD_LABELS] ?? proof.method,
    proof.reference,
    formatDate(proof.submittedAt),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rounded-xl border border-line bg-canvas px-2.5 py-2.5">
      <div className="min-w-0">
        <p className="text-xs font-medium text-ink">
          {proof.hasImage ? "Screenshot" : "Transfer details"}
        </p>
        <p className="mt-0.5 text-[11px] text-ink-subtle">{meta}</p>
        {proof.note ? (
          <p className="mt-1 text-xs leading-relaxed text-ink-muted">{proof.note}</p>
        ) : null}
        {proof.hasImage ? (
          <ProofImage src={imageUrl} caption={meta} asLink />
        ) : null}
      </div>
    </div>
  );
}

/** Opens the transfer screenshot in a lightbox — text link or small thumbnail. */
export function ProofImage({
  src,
  caption,
  asLink = false,
}: {
  src: string;
  caption?: string;
  /** Text link instead of an inline thumbnail. */
  asLink?: boolean;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {asLink ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="View the screenshot of the transfer"
          className="mt-1.5 text-xs font-semibold text-brand-600 underline underline-offset-2 transition hover:text-ink"
        >
          View screenshot
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="View the screenshot of the transfer"
          className="group relative size-11 shrink-0 cursor-zoom-in overflow-hidden rounded-lg ring-1 ring-line transition hover:ring-line-strong"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={src} alt="" className="size-full bg-surface object-cover object-top" />
          <span className="absolute inset-0 flex items-center justify-center bg-ink/50 text-white opacity-0 transition group-hover:opacity-100">
            <ZoomIcon />
          </span>
        </button>
      )}

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Screenshot of the transfer"
            onClick={(event) => event.stopPropagation()}
            className="relative flex max-h-full flex-col items-center gap-2"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute -top-1 -right-1 z-10 inline-flex size-9 items-center justify-center rounded-full bg-ink/80 text-white shadow-lg ring-1 ring-white/20 transition hover:bg-ink"
            >
              <CloseIcon />
            </button>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt="Screenshot of the transfer"
              className="max-h-[80dvh] w-auto max-w-full rounded-xl bg-surface shadow-2xl"
            />
            {caption ? <p className="text-xs text-white/70">{caption}</p> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

function ZoomIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
      <circle cx="8.75" cy="8.75" r="4.75" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12.5 12.5 16 16M6.75 8.75h4M8.75 6.75v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="size-4" aria-hidden="true">
      <path
        d="M5 5l10 10M15 5 5 15"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
